import {
  Area,
  AreaChart,
  CartesianGrid,
  DotProps,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

export interface GraphData {
  name: string;
  value: number;
}

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }: TooltipProps<any, any>) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary text-white p-2 rounded-lg text-xs">
        <p style={{ margin: 0 }}>
          <strong className="">{payload[0].payload.name}:</strong> {payload[0].payload.value.toLocaleString("de-DE")}€
        </p>
      </div>
    );
  }
  return null;
};

const CustomDot = (props: DotProps) => {
  const { cx, cy } = props;
  if (!cx || !cy) return null;

  return (
    <g>
      {/* Outer translucent circle */}
      <circle cx={cx} cy={cy} r={8} fill="none" stroke="#12282A" strokeWidth={2} opacity={0.2} />
      {/* Inner gradient-filled circle */}
      <defs>
        <linearGradient id="dotGradientBig" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C0DE60" />
          <stop offset="100%" stopColor="#5FD5DE" />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={4} fill="url(#dotGradientBig)" />
      <circle cx={cx} cy={cy} r={4} fill="none" stroke="#12282A" strokeWidth={1.5} />
    </g>
  );
};

// Function to format Y-axis ticks with German formatting
const formatYAxisTick = (value: number) => {
  return `${value.toLocaleString("de-DE")} €`;
};

// Function to format X-axis ticks to show month abbreviations
const formatXAxisTick = (value: string) => {
  // Extract month from the date range or use the value directly if it's already a month
  if (value.includes("-")) {
    const month = value.split(" - ")[0];
    const monthNumber = parseInt(month.split(".")[1]);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return monthNames[monthNumber - 1] || value;
  }
  return value;
};

const GraphBig = ({ data }: { data: GraphData[] }) => {
  // Calculate the maximum value to set appropriate domain
  const maxValue = Math.max(...data.map((item) => item.value));
  const yAxisMax = Math.ceil(maxValue / 300) * 300; // Round up to nearest 300 for more grid lines

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ right: 20, top: 10 }}>
        {/* Gradient Definitions */}
        <defs>
          <linearGradient id="fillGradientBig" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C0DE60" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#5FD5DE" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id="strokeGradientBig" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C0DE60" stopOpacity={1} />
            <stop offset="100%" stopColor="#5FD5DE" stopOpacity={1} />
          </linearGradient>
        </defs>

        {/* Grid Lines */}
        <CartesianGrid strokeDasharray="none" stroke="#e5e7eb" strokeWidth={1} horizontal={true} vertical={false} />

        {/* X Axis */}
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
          tickFormatter={formatXAxisTick}
          interval={0}
        />

        {/* Y Axis */}
        <YAxis
          domain={[0, yAxisMax]}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
          tickFormatter={formatYAxisTick}
          width={70}
          tickCount={7}
        />

        {/* Area Chart */}
        <Area
          type="linear"
          dataKey="value"
          stroke="url(#strokeGradientBig)"
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#fillGradientBig)"
          dot={false}
          activeDot={<CustomDot />}
        />

        {/* Custom Tooltip */}
        <Tooltip content={<CustomTooltip />} cursor={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default GraphBig;
